import { Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Loader2, Plus, Truck, X } from "lucide-react";
import { api, apiError, fmt, fmtDate } from "@/lib/api";

const EMPTY = { name: "", contact_person: "", email: "", phone: "", gstin: "", address: "", active: true };

function SupplierForm({ initial, onSave, onClose, busy }) {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const field = "h-11 w-full rounded-xl border border-white/10 bg-black/40 px-3.5 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";
  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="supplier-form"
        className="w-full max-w-md rounded-2xl border border-white/10 bg-[#FFFEFA] p-6">
        <div className="flex items-start justify-between">
          <h3 className="font-display text-lg font-semibold text-white">{initial.id ? "Edit supplier" : "New supplier"}</h3>
          <button onClick={onClose} aria-label="Close" className="text-neutral-500 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-4 space-y-3">
          <input value={f.name} onChange={set("name")} placeholder="Supplier / company name *" data-testid="supplier-name" className={field} />
          <div className="grid grid-cols-2 gap-3">
            <input value={f.contact_person} onChange={set("contact_person")} placeholder="Contact person" data-testid="supplier-contact" className={field} />
            <input value={f.phone} onChange={set("phone")} placeholder="Phone" data-testid="supplier-phone" className={field} />
          </div>
          <input value={f.email} onChange={set("email")} type="email" placeholder="Email" data-testid="supplier-email" className={field} />
          <input value={f.gstin} onChange={set("gstin")} placeholder="GSTIN (optional)" data-testid="supplier-gstin" className={field} />
          <input value={f.address} onChange={set("address")} placeholder="Address" data-testid="supplier-address" className={field} />
        </div>
        <button onClick={() => onSave(f)} disabled={busy || !f.name.trim()} data-testid="supplier-save"
          className="mt-5 flex h-11 w-full items-center justify-center gap-2 rounded-full bg-amber-500 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-40">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save supplier
        </button>
      </div>
    </div>
  );
}

export default function AdminSuppliers() {
  const [suppliers, setSuppliers] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [form, setForm] = useState(null); // null | {} | supplier
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.get("/admin/suppliers"), api.get("/admin/purchases")])
      .then(([s, p]) => { setSuppliers(s.data.suppliers); setPurchases(p.data.purchases); })
      .catch((e) => toast.error(apiError(e, "Couldn't load suppliers")));
  }, []);
  useEffect(load, [load]);

  const save = async (f) => {
    setBusy(true);
    try {
      const payload = { ...f, email: f.email || null };
      if (f.id) await api.put(`/admin/suppliers/${f.id}`, payload);
      else await api.post("/admin/suppliers", payload);
      toast.success(f.id ? "Supplier updated" : "Supplier created");
      setForm(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Couldn't save the supplier"));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/admin/suppliers/${s.id}`, { name: s.name, contact_person: s.contact_person || "", email: s.email || null,
        phone: s.phone || "", gstin: s.gstin || "", address: s.address || "", active: !s.active });
      toast.success(s.active ? `${s.name} disabled` : `${s.name} re-enabled`);
      load();
    } catch (e) { toast.error(apiError(e, "Couldn't update the supplier")); }
  };

  return (
    <div data-testid="admin-suppliers" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-white"><Truck className="h-6 w-6 text-amber-400" /> Suppliers</h1>
          <p className="mt-1 text-sm text-neutral-500">Vendors you purchase stock from.</p>
        </div>
        <button onClick={() => setForm(EMPTY)} data-testid="supplier-add-btn"
          className="flex h-11 items-center gap-2 rounded-full bg-amber-500 px-5 text-sm font-bold text-black transition-colors hover:bg-amber-400">
          <Plus className="h-4 w-4" /> New supplier
        </button>
      </div>

      {!suppliers ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>
      ) : suppliers.length === 0 ? (
        <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] py-16 text-center text-sm text-neutral-500" data-testid="suppliers-empty">
          No suppliers yet — add your first vendor.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-white/[0.07]">
          <table className="w-full text-left text-sm" data-testid="suppliers-table">
            <thead>
              <tr className="border-b border-white/[0.07] bg-white/[0.03] text-[11px] uppercase tracking-wider text-neutral-500">
                <th className="w-8 px-3 py-3"></th><th className="px-4 py-3">Supplier</th><th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">GSTIN</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((s) => (
                <Fragment key={s.id}>
                  <tr className="border-b border-white/[0.05] hover:bg-white/[0.02]" data-testid={`supplier-row-${s.id}`}>
                    <td className="px-3 py-3">
                      <button onClick={() => setExpanded(expanded === s.id ? null : s.id)} aria-label="Purchase history"
                        data-testid={`supplier-history-${s.id}`} className="text-neutral-500 hover:text-amber-300">
                        {expanded === s.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-white">{s.name}</p>
                      {s.address && <p className="max-w-56 truncate text-xs text-neutral-500">{s.address}</p>}
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-400">
                      {s.contact_person && <p>{s.contact_person}</p>}
                      {s.email && <p>{s.email}</p>}
                      {s.phone && <p>{s.phone}</p>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-neutral-400">{s.gstin || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${s.active ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-red-500/30 bg-red-500/10 text-red-400"}`}
                        data-testid={`supplier-status-${s.id}`}>
                        {s.active ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setForm({ ...EMPTY, ...s })} data-testid={`supplier-edit-${s.id}`}
                          className="h-8 rounded-full border border-white/15 px-3 text-[11px] font-semibold text-neutral-300 hover:text-white">Edit</button>
                        <button onClick={() => toggleActive(s)} data-testid={`supplier-toggle-${s.id}`}
                          className={`h-8 rounded-full border px-3 text-[11px] font-semibold transition-colors ${s.active ? "border-white/15 text-neutral-300 hover:border-red-500/40 hover:text-red-300" : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"}`}>
                          {s.active ? "Disable" : "Enable"}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expanded === s.id && (
                    <tr className="border-b border-white/[0.05] bg-black/30" data-testid={`supplier-history-panel-${s.id}`}>
                      <td colSpan={6} className="px-6 py-4">
                        <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-neutral-500">Purchase history</p>
                        {purchases.filter((p) => p.supplier_id === s.id).length === 0 ? (
                          <p className="text-xs text-neutral-600">No purchases from this supplier yet.</p>
                        ) : (
                          <div className="space-y-1.5">
                            {purchases.filter((p) => p.supplier_id === s.id).map((p) => (
                              <p key={p.id} className="text-xs text-neutral-400">
                                <span className="font-mono font-bold text-amber-300/80">{p.purchase_number}</span> · {p.purchase_date} ·{" "}
                                {p.items.reduce((n, i) => n + i.qty, 0)} pcs · {fmt(p.total)} ·{" "}
                                <span className={p.status === "received" ? "text-emerald-400" : "text-amber-300"}>{p.status}</span>
                              </p>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {form && <SupplierForm initial={form} busy={busy} onSave={save} onClose={() => setForm(null)} />}
    </div>
  );
}
